import { useCallback, useRef, useMemo } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import type { EInvoiceRecord } from '@/api/compliance.api';
import { getEInvoicePublicVerifyUrl } from '@/utils/gstCalculation';

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

interface QRCodeViewerModalProps {
  ei: EInvoiceRecord | any;
  onClose: () => void;
}

export default function QRCodeViewerModal({ ei, onClose }: QRCodeViewerModalProps) {
  const canvasRef = useRef<HTMLDivElement>(null);

  const qrVerifyUrl = useMemo(() => {
    return getEInvoicePublicVerifyUrl(ei.irn || ei.ackNumber || ei.invoiceNumber);
  }, [ei]);

  // The QR encodes our public verify page URL, not the raw NIC-signed JWT — scanning a
  // JWT with an ordinary phone camera just shows encoded text, readable only by a
  // specialized e-invoice verifier app. A URL is universally scannable and opens a real,
  // human-readable page (seller, buyer, items, totals, IRN, live status) that anyone can see.
  const qrValue = qrVerifyUrl;

  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `EInvoice_${ei.invoiceNumber}_${ei.ackNumber || 'QR'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  }, [ei.invoiceNumber, ei.ackNumber]);

  const handlePrint = useCallback(() => {
    const canvas = canvasRef.current?.querySelector('canvas');
    const dataUrl = canvas ? canvas.toDataURL('image/png') : '';
    const printWindow = window.open('', '_blank', 'width=400,height=520');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - ${ei.invoiceNumber}</title>
            <style>
              body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; font-family: sans-serif; }
              img { width: 220px; height: 220px; }
              .irn { font-size: 9px; word-break: break-all; max-width: 260px; text-align: center; margin-top: 8px; color: #444; }
              .ack { font-size: 11px; font-weight: bold; margin-top: 4px; }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <h3 style="margin: 0 0 8px 0; font-size: 14px;">GST E-Invoice QR Code</h3>
            <p style="margin: 0 0 12px 0; font-size: 11px; color: #666;">Invoice: ${ei.invoiceNumber}</p>
            <img src="${dataUrl}" alt="E-Invoice QR" />
            <div class="ack">Ack No: ${ei.ackNumber || '—'}</div>
            <div class="irn">IRN: ${ei.irn || '—'}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  }, [ei]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 flex flex-col items-center relative animate-in fade-in zoom-in-95 duration-150 border border-slate-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
          <i className="ri-qr-code-line text-xl" />
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-0.5">GST E-Invoice QR Code</h3>
        <p className="text-xs text-gray-500 mb-4 text-center">
          Invoice <span className="font-semibold text-gray-700">{ei.invoiceNumber}</span>
        </p>

        {/* Scan-to-verify QR — encodes the public verification page URL, readable by any camera */}
        <div ref={canvasRef} className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm mb-3 flex flex-col items-center">
          <QRCodeCanvas
            value={qrValue}
            size={200}
            level="M"
            marginSize={2}
          />
          <span className="text-[10px] text-slate-500 mt-2 font-medium">
            Scan with any phone camera to view the verified invoice
          </span>
        </div>

        {/* Direct Link Preview */}
        <a
          href={qrVerifyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 mb-3 underline cursor-pointer"
        >
          <i className="ri-external-link-line" /> Open Verification URL in Browser
        </a>

        <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 mb-4 text-xs space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-slate-500">IRN Hash</span>
            <span className="text-slate-800 font-mono text-[11px] truncate max-w-[170px]" title={ei.irn}>
              {(ei.irn || '').slice(0, 10)}...{(ei.irn || '').slice(-8)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500">Ack Number</span>
            <span className="text-slate-800 font-mono font-semibold">{ei.ackNumber || '—'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500">Ack Date</span>
            <span className="text-slate-800">{formatDateTime(ei.ackDate)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full">
          <button
            onClick={handleDownload}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 cursor-pointer whitespace-nowrap shadow-sm"
          >
            <i className="ri-download-line text-sm" />
            Download PNG
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap shadow-sm"
          >
            <i className="ri-printer-line text-sm" />
            Print QR
          </button>
        </div>

        <button
          onClick={onClose}
          className="mt-3 w-full py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
        >
          Close
        </button>
      </div>
    </div>
  );
}